import { fetcherWithToken } from '@/shared/api/fetcher';
import { use, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import useSWR, { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';

async function setBonus(url, { arg }: { arg: string }) {
  return fetcherWithToken(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
}

const useCurrency = () => {
  const { data: currencies, isLoading: isCurrenciesLoading } = useSWR(
    '/payment/crypto/currencies',
    fetcherWithToken
  );

  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (currencies) {
      setFilteredCurrencies(currencies);
    }
  }, [currencies]);

  const handleSearch = useCallback(
    debounce((text) => {
      if (!currencies) return;

      const lowercasedText = text.toLowerCase();
      const filtered = currencies.filter(
        (currency) =>
          currency.code.toLowerCase().includes(lowercasedText) ||
          currency.name.toLowerCase().includes(lowercasedText)
      );
      setFilteredCurrencies(filtered);
    }, 300),
    [currencies]
  );
  const handleInputChange = (text) => {
    setSearchText(text);
    handleSearch(text);
  };

  const handleClear = () => {
    setSearchText('');
    setFilteredCurrencies(currencies || []);
  };

  return {
    currencies: filteredCurrencies,
    isCurrenciesLoading,
    handleClear,
    handleSearch: handleInputChange,
    searchText,
  };
};

const cryptoPayment = async (url, { arg }) => {
  return fetcherWithToken(url, {
    method: 'POST',
    body: JSON.stringify(arg),
  });
};

const useCurrencyAddress = ({ code, bonus }) => {
  const { cache } = useSWRConfig();
  const {
    data: currencyAddress,
    error,
    isLoading: currencyAddressLoading,
    mutate: mutateCurrencyAddress,
  } = useSWR(`/payment/crypto/payment`, (url) =>
    cryptoPayment(url, { arg: { currency: code, bonus } })
  );

  useEffect(() => {
    return () => {
      cache.delete('/payment/crypto/payment');
      mutateCurrencyAddress();
    };
  }, []);

  const { trigger: setBonusTrigger } = useSWRMutation(
    '/payment/setBonus',
    setBonus
  );

  return {
    currencyAddress,
    currencyAddressLoading,
    currencyAddressError: error,
    setBonusTrigger,
  };
};

export { useCurrencyAddress, useCurrency };
